import React, { useState, useRef, useEffect } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Button, Container, Link, TextField, Typography } from '@material-ui/core';
import ReCAPTCHA from "react-google-recaptcha";
import { forgetPass, verifyToken } from '../../services/ForgetPass';
import { getPublicIp } from '../../utils/index';
import appConfig from '../../config/main.config';
import { useTranslation } from 'react-i18next';

const ForgetPassword = () => {
  const {t} = useTranslation();
  const captcha = useRef(null);
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(useLocation().search);
  const token = urlParams.get('t');

  const [errValidUser, setErrValidUser] = useState(null);
  const [validCaptcha, setValidCaptcha] = useState(null);
  const [tokensent, setTokensent] = useState(false);
  const [errorToken, setErrorToken] = useState(false);

  const [disabledButton, setDisabledButton] = useState(true);

  const onChangeCaptcha = () => {
    if (captcha.current.getValue()) {
      //console.log('no es robot', captcha.current.getValue())
      setValidCaptcha(true);
      setDisabledButton(false);
    } else {
      setValidCaptcha(false);
      setDisabledButton(true);
    }
  }

  useEffect(() => {
    if (token !== null) {

      (async function () {
        //verificar validez token
        const validToken = await verifyToken(token);
        //console.log('token valid', validToken);

        if (validToken.valid) {
          navigate('/reset/password/', { state: { token: token } })
        } else {
          //console.log('no es válido');
          setErrorToken(true);
        }

      })();

    }
  }, [])


  if (token === null) {
    return (
      <>
        <Helmet>
          <title>{t('resetPasswordPageTitle')}</title>
        </Helmet>
        <Box
          sx={{
            /*backgroundColor: 'background.default',*/
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center'
          }}
        >

          {tokensent ?

            <Container maxWidth="sm" variant='cont-nk'>

              <Box sx={{ mb: 2, mt: 3 }}>
                <Typography
                  color="textPrimary"
                  variant="h2"
                  align="center"
                >
                  {t('passwordResetEmailSent')}
                </Typography>

              </Box>

              <Box sx={{ pt: 1, pb: 2, textAlign: 'center' }}>
                <Typography
                  color="textSecondary"
                  gutterBottom
                  variant="body1"
                >
                  {t('passwordResetSpamNotice')}
                </Typography>

                <Link
                  component={RouterLink}
                  to="/login/"
                  variant="h6"
                >
                  {t('goToLogin')}
                </Link>
              </Box>

            </Container>

            :

            <Container maxWidth="sm" variant='cont-nk'>
              <Formik
                initialValues={{
                  email: ''
                }}
                validationSchema={Yup.object().shape({
                  email: Yup.string().email(()=>t('invalidEmail')).max(255).required(()=>t('emailRequired'))
                })}

                onSubmit={async (val, actions) => {

                  if (captcha.current.getValue() !== '') {
                    const ipv4 = await getPublicIp();
                    const res = await forgetPass(val.email, ipv4);

                    actions.resetForm();
                    setTokensent(true);

                  } else {
                    setValidCaptcha(false);
                  }


                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 2, mt: 3 }}>
                      <Typography
                        color="textPrimary"
                        variant="h2"
                        align="center"
                      >
                        {t('resetPassword')}
                      </Typography>

                    </Box>

                    <Box sx={{ pt: 1, pb: 2 }}>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        variant="body1"
                        align="center"
                      >
                        {t('resetPasswordDescription')}
                      </Typography>
                    </Box>

                    {errValidUser &&
                      <Box>
                        <Typography sx={{ color: 'red' }} variant="h6">
                          {errValidUser}
                        </Typography>
                      </Box>
                    }


                    <TextField
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      label={t('emailAddress')}
                      margin="normal"
                      name="email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="email"
                      value={values.email}
                      variant="outlined"
                    />

                    <Box align='center' sx={{ py: 2 }}>
                      <ReCAPTCHA
                        ref={captcha}
                        sitekey={appConfig.sitekey_captcha}
                        onChange={onChangeCaptcha}
                      />
                      {validCaptcha === false &&
                        <Typography sx={{ color: 'red' }} variant="h6">
                          {t('acceptCaptcha')}
                        </Typography>
                      }

                    </Box>

                    <Box sx={{ py: 3 }}>
                      <Button
                        color="primary"
                        disabled={disabledButton}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                      >
                        {t('continue')}
                      </Button>
                    </Box>
                    <Box sx={{ paddingBottom: 3 }}>
                      <Typography
                        color="textSecondary"
                        variant="body1"
                        align='center'
                      >
                        <Link
                          component={RouterLink}
                          to="/login/"
                          variant="h6"
                        >
                          {t('backToLogin')}
                        </Link>
                      </Typography>
                    </Box>

                  </form>
                )}
              </Formik>
            </Container>

          }


        </Box>
      </>
    );
  } else {
    if (errorToken) {

      return (
        <>
          <Helmet>
            <title>{t('resetPasswordPageTitle')}</title>
          </Helmet>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'center'
            }}
          >

            <Container maxWidth="sm" variant='cont-nk'>

              <Box sx={{ mb: 2, mt: 3 }}>
                <Typography
                  color="textPrimary"
                  variant="h2"
                  align="center"
                >
                  {t('sorry')}
                </Typography>

              </Box>

              <Box sx={{ pt: 1, pb: 2 }}>
                <Typography
                  color="textSecondary"
                  gutterBottom
                  variant="body1"
                  align="center"
                >
                  {t('passwordResetTokenExpired')}
                </Typography>

                <Typography
                  color="textSecondary"
                  variant="body1"
                  align='center'
                >
                  <Link
                    component={RouterLink}
                    to="/reset/"
                    variant="h6"
                  >
                    {t('tryAgain')}
                  </Link>
                </Typography>
              </Box>

            </Container>
          </Box>
        </>

      )

    } else {
      return (
        <Typography variant='body2' style={{ textAlign: 'center' }}>{t('validatingData')}</Typography>
      )
    }

  }


};

export default ForgetPassword;
